import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

/**
 * Permission Context
 * Manages user permissions and provides permission checking utilities
 */

interface PermissionContextType {
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isLoading: boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();

  /**
   * Fetch user permissions from backend
   * Permissions are derived from role + user-specific overrides
   */
  const fetchPermissions = async () => {
    if (!isAuthenticated || !user) {
      setPermissions([]);
      setIsLoading(false);
      return;
    }

    try {
      // In a real implementation, you'd have an endpoint like /users/me/permissions
      // For now, we'll get permissions from the user profile
      const response = await axios.get('/auth/profile');
      const userProfile = response.data;

      // Extract permissions from role and user permissions
      const rolePermissions: string[] = userProfile.role?.permissions?.map(
        (rp: any) => rp.permission.code
      ) || [];

      // Apply user-specific permission overrides
      const userPermissions = userProfile.userPermissions || [];
      const permissionSet = new Set(rolePermissions);

      userPermissions.forEach((up: any) => {
        if (up.isGranted) {
          permissionSet.add(up.permission.code);
        } else {
          permissionSet.delete(up.permission.code);
        }
      });

      setPermissions(Array.from(permissionSet));
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [isAuthenticated, user]);

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    
    // SuperAdmin and Admin have all permissions
    if (user.role.code === 'SUPER_ADMIN' || user.role.code === 'ADMIN') {
      return true;
    }

    return permissions.includes(permission);
  };

  const hasAnyPermission = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.some((permission) => hasPermission(permission));
  };

  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.every((permission) => hasPermission(permission));
  };

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        isLoading,
        refreshPermissions: fetchPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};



